import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiX,
  FiSave,
  FiPlus,
  FiTrash2,
  FiAlertCircle,
} from 'react-icons/fi';
import {
  ArchitectureVariable,
  CreateVariableData,
  UpdateVariableData,
  VariableScope,
  VariableType,
  ValidationRules,
  createVariable,
  updateVariable,
} from '../../api/variables';

interface VariableModalProps {
  architectureId: string;
  variable: ArchitectureVariable | null;
  onClose: () => void;
  onSuccess: () => void;
}

const VariableModal: React.FC<VariableModalProps> = ({
  architectureId,
  variable,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('canvas');
  const isEditing = !!variable;

  // Form state
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<VariableScope>('parameter');
  const [type, setType] = useState<VariableType>('string');
  const [category, setCategory] = useState('General');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isSensitive, setIsSensitive] = useState(false);

  // Default value
  const [defaultValueType, setDefaultValueType] = useState<'value' | 'expression'>('value');
  const [defaultValue, setDefaultValue] = useState('');
  const [defaultExpression, setDefaultExpression] = useState('');

  // Validation rules
  const [required, setRequired] = useState(false);
  const [min, setMin] = useState<number | ''>('');
  const [max, setMax] = useState<number | ''>('');
  const [minLength, setMinLength] = useState<number | ''>('');
  const [maxLength, setMaxLength] = useState<number | ''>('');
  const [pattern, setPattern] = useState('');
  const [enumValues, setEnumValues] = useState<string[]>([]);
  const [newEnumValue, setNewEnumValue] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  // Initialize form with variable data
  useEffect(() => {
    if (variable) {
      setName(variable.name);
      setDisplayName(variable.display_name || '');
      setDescription(variable.description || '');
      setScope(variable.scope);
      setType(variable.type);
      setCategory(variable.category || 'General');
      setDisplayOrder(variable.display_order || 0);
      setIsSensitive(variable.is_sensitive || false);

      // Default value
      if (variable.default_value?.expression) {
        setDefaultValueType('expression');
        setDefaultExpression(variable.default_value.expression);
      } else if (variable.default_value?.value !== undefined) {
        setDefaultValueType('value');
        setDefaultValue(
          typeof variable.default_value.value === 'object'
            ? JSON.stringify(variable.default_value.value)
            : String(variable.default_value.value)
        );
      }

      // Validation rules
      const rules = variable.validation_rules || {};
      setRequired(rules.required || false);
      setMin(rules.min ?? '');
      setMax(rules.max ?? '');
      setMinLength(rules.minLength ?? '');
      setMaxLength(rules.maxLength ?? '');
      setPattern(rules.pattern || '');
      setEnumValues(rules.enum || []);
    }
  }, [variable]);

  // Validate name format
  const validateName = (value: string) => {
    if (!value) {
      setNameError(t('variables.nameRequired', 'Name is required'));
      return false;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(value)) {
      setNameError(t('variables.nameFormat', 'Name must be snake_case (lowercase, underscores, start with letter)'));
      return false;
    }
    setNameError(null);
    return true;
  };

  // Handle name change
  const handleNameChange = (value: string) => {
    setName(value);
    if (value) {
      validateName(value);
    } else {
      setNameError(null);
    }
  };

  // Add enum value
  const addEnumValue = () => {
    if (newEnumValue && !enumValues.includes(newEnumValue)) {
      setEnumValues([...enumValues, newEnumValue]);
      setNewEnumValue('');
    }
  };

  // Remove enum value
  const removeEnumValue = (value: string) => {
    setEnumValues(enumValues.filter((v) => v !== value));
  };

  // Build validation rules object
  const buildValidationRules = (): ValidationRules => {
    const rules: ValidationRules = {};
    if (required) rules.required = true;
    if (min !== '') rules.min = Number(min);
    if (max !== '') rules.max = Number(max);
    if (minLength !== '') rules.minLength = Number(minLength);
    if (maxLength !== '') rules.maxLength = Number(maxLength);
    if (pattern) rules.pattern = pattern;
    if (enumValues.length > 0) rules.enum = enumValues;
    return rules;
  };

  // Build default value object
  const buildDefaultValue = () => {
    if (defaultValueType === 'expression' && defaultExpression) {
      return { expression: defaultExpression };
    }
    if (defaultValueType === 'value' && defaultValue) {
      // Parse value based on type
      let parsedValue: any = defaultValue;
      switch (type) {
        case 'number':
          parsedValue = Number(defaultValue);
          break;
        case 'boolean':
          parsedValue = defaultValue === 'true';
          break;
        case 'json':
          try {
            parsedValue = JSON.parse(defaultValue);
          } catch {
            // Keep as string if not valid JSON
          }
          break;
      }
      return { value: parsedValue };
    }
    return undefined;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateName(name)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const validationRules = buildValidationRules();
      const defaultValueObj = buildDefaultValue();

      if (isEditing && variable) {
        const data: UpdateVariableData = {
          displayName: displayName || undefined,
          description: description || undefined,
          type,
          defaultValue: defaultValueObj,
          validationRules: Object.keys(validationRules).length > 0 ? validationRules : undefined,
          category,
          displayOrder,
          isSensitive,
        };
        await updateVariable(architectureId, variable.id, data);
      } else {
        const data: CreateVariableData = {
          name,
          displayName: displayName || undefined,
          description: description || undefined,
          scope,
          type,
          defaultValue: defaultValueObj,
          validationRules: Object.keys(validationRules).length > 0 ? validationRules : undefined,
          category,
          displayOrder,
          isSensitive,
        };
        await createVariable(architectureId, data);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving variable:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save variable');
    } finally {
      setSaving(false);
    }
  };

  // Common categories
  const categories = ['General', 'Infrastructure', 'Network', 'Security', 'Database', 'Compute', 'Storage'];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 560,
          maxHeight: '90vh',
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 20,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#111827' }}>
            {isEditing
              ? t('variables.editTitle', 'Edit Variable')
              : t('variables.createTitle', 'Create Variable')}
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: 8,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 4,
            }}
          >
            <FiX size={20} style={{ color: '#6B7280' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {error && (
            <div
              style={{
                padding: 12,
                background: '#FEE2E2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FiAlertCircle size={18} style={{ color: '#DC2626' }} />
              <span style={{ color: '#DC2626', fontSize: 14 }}>{error}</span>
            </div>
          )}

          {/* Basic Info */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              {t('variables.basicInfo', 'Basic Information')}
            </h3>

            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                {t('variables.name', 'Name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                disabled={isEditing}
                placeholder="my_variable"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${nameError ? '#EF4444' : '#E5E7EB'}`,
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'monospace',
                  background: isEditing ? '#F9FAFB' : 'white',
                }}
              />
              {nameError && (
                <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>{nameError}</p>
              )}
            </div>

            {/* Display Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                {t('variables.displayName', 'Display Name')}
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="My Variable"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                {t('variables.description', 'Description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('variables.descriptionPlaceholder', 'Describe what this variable is for...')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                  resize: 'vertical',
                }}
              />
            </div>

            {/* Scope & Type row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Scope */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  {t('variables.scope', 'Scope')} *
                </label>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value as VariableScope)}
                  disabled={isEditing}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                    background: isEditing ? '#F9FAFB' : 'white',
                  }}
                >
                  <option value="parameter">{t('variables.scopeParameter', 'Parameter (exposed)')}</option>
                  <option value="variable">{t('variables.scopeVariable', 'Variable (internal)')}</option>
                </select>
                <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                  {scope === 'parameter'
                    ? t('variables.scopeParameterHelp', 'Users can override this value')
                    : t('variables.scopeVariableHelp', 'Internal, auto-calculated value')}
                </p>
              </div>

              {/* Type */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  {t('variables.type', 'Type')} *
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as VariableType)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="select">Select (dropdown)</option>
                  <option value="multiselect">Multi-select</option>
                  <option value="json">JSON</option>
                  <option value="reference">Reference</option>
                </select>
              </div>
            </div>

            {/* Category & Order row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Category */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  {t('variables.category', 'Category')}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Display Order */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  {t('variables.order', 'Order')}
                </label>
                <input
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                />
              </div>
            </div>

            {/* Sensitive toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isSensitive}
                onChange={(e) => setIsSensitive(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13, color: '#374151' }}>
                {t('variables.isSensitive', 'Sensitive value (will be masked in logs)')}
              </span>
            </label>
          </div>

          {/* Default Value */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              {t('variables.defaultValue', 'Default Value')}
            </h3>

            {/* Value type selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button
                onClick={() => setDefaultValueType('value')}
                style={{
                  padding: '8px 16px',
                  border: `1px solid ${defaultValueType === 'value' ? '#6366F1' : '#E5E7EB'}`,
                  borderRadius: 6,
                  background: defaultValueType === 'value' ? '#EEF2FF' : 'white',
                  color: defaultValueType === 'value' ? '#6366F1' : '#6B7280',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t('variables.staticValue', 'Static Value')}
              </button>
              <button
                onClick={() => setDefaultValueType('expression')}
                style={{
                  padding: '8px 16px',
                  border: `1px solid ${defaultValueType === 'expression' ? '#6366F1' : '#E5E7EB'}`,
                  borderRadius: 6,
                  background: defaultValueType === 'expression' ? '#EEF2FF' : 'white',
                  color: defaultValueType === 'expression' ? '#6366F1' : '#6B7280',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                {t('variables.expression', 'Expression')}
              </button>
            </div>

            {defaultValueType === 'value' ? (
              <div>
                {type === 'boolean' ? (
                  <select
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      fontSize: 14,
                    }}
                  >
                    <option value="">-</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : type === 'json' ? (
                  <textarea
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    placeholder='{"key": "value"}'
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      fontSize: 13,
                      fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                ) : (
                  <input
                    type={type === 'number' ? 'number' : 'text'}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    placeholder={type === 'number' ? '0' : 'default value'}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      fontSize: 14,
                    }}
                  />
                )}
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={defaultExpression}
                  onChange={(e) => setDefaultExpression(e.target.value)}
                  placeholder="${azurerm_resource.name.id}"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    fontSize: 13,
                    fontFamily: 'monospace',
                  }}
                />
                <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                  {t('variables.expressionHelp', 'Use Terraform expressions like ${resource.attribute}')}
                </p>
              </div>
            )}
          </div>

          {/* Validation Rules */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
              {t('variables.validation', 'Validation Rules')}
            </h3>

            {/* Required */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13, color: '#374151' }}>
                {t('variables.required', 'Required')}
              </span>
            </label>

            {/* Number validation */}
            {type === 'number' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {t('variables.minValue', 'Min value')}
                  </label>
                  <input
                    type="number"
                    value={min}
                    onChange={(e) => setMin(e.target.value ? Number(e.target.value) : '')}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {t('variables.maxValue', 'Max value')}
                  </label>
                  <input
                    type="number"
                    value={max}
                    onChange={(e) => setMax(e.target.value ? Number(e.target.value) : '')}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
            )}

            {/* String validation */}
            {(type === 'string' || type === 'select') && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                      {t('variables.minLength', 'Min length')}
                    </label>
                    <input
                      type="number"
                      value={minLength}
                      onChange={(e) => setMinLength(e.target.value ? Number(e.target.value) : '')}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #E5E7EB',
                        borderRadius: 6,
                        fontSize: 13,
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                      {t('variables.maxLength', 'Max length')}
                    </label>
                    <input
                      type="number"
                      value={maxLength}
                      onChange={(e) => setMaxLength(e.target.value ? Number(e.target.value) : '')}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid #E5E7EB',
                        borderRadius: 6,
                        fontSize: 13,
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {t('variables.pattern', 'Pattern (regex)')}
                  </label>
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    placeholder="^[a-z0-9-]+$"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'monospace',
                    }}
                  />
                </div>
              </>
            )}

            {/* Enum values for select types */}
            {(type === 'select' || type === 'multiselect') && (
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                  {t('variables.allowedValues', 'Allowed values')}
                </label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="text"
                    value={newEnumValue}
                    onChange={(e) => setNewEnumValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEnumValue();
                      }
                    }}
                    placeholder={t('variables.addValue', 'Add value...')}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      border: '1px solid #E5E7EB',
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                  <button
                    onClick={addEnumValue}
                    style={{
                      padding: '8px 12px',
                      background: '#F3F4F6',
                      border: '1px solid #E5E7EB',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    <FiPlus size={16} />
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {enumValues.map((val) => (
                    <span
                      key={val}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        background: '#EEF2FF',
                        border: '1px solid #C7D2FE',
                        borderRadius: 6,
                        fontSize: 12,
                        color: '#4F46E5',
                      }}
                    >
                      {val}
                      <button
                        onClick={() => removeEnumValue(val)}
                        style={{
                          padding: 0,
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                        }}
                      >
                        <FiTrash2 size={12} style={{ color: '#EF4444' }} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 16,
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t('variables.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !!nameError}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              background: saving || nameError ? '#9CA3AF' : '#6366F1',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: saving || nameError ? 'not-allowed' : 'pointer',
            }}
          >
            <FiSave size={16} />
            {saving ? t('variables.saving', 'Saving...') : t('variables.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VariableModal;
